import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Line, LineChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import PickCard from 'components/PickCard/PickCard';
import PicksFilters from 'components/PicksFilters/PicksFilters';
import Spinner from 'components/Spinner/Spinner';
import { listPicks } from 'services/picksApi';
import { listGames } from 'services/gamesApi';
import { PickDetailGameInfo } from 'pages/PickDetail/PickDetail';
import { DEFAULT_SPORT, GROUP_NAMES } from 'utils';
import type { ApiPick, PicksFilterValues, ApiGame, ApiGameListResponse } from 'services/types';
import { t } from '../../helpers/componentHelper';

import './PickInsights.scss';

type PickInsightsComponentProps = {
  userGroups: string[];
};

// Top-level PickInsights page — backed by the real /api/v1/picks endpoint.
function PickInsights({ userGroups }: PickInsightsComponentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location?.state as { currentFilters?: PicksFilterValues } | undefined;
  const [filters, setFilters] = useState<PicksFilterValues>({
    ...(state?.currentFilters ?? {
      sport: DEFAULT_SPORT,
      riskLevel: 'lock',
    }),
  });
  const [picks, setPicks] = useState<ApiPick[]>([]);
  const [allPicks, setAllPicks] = useState<ApiPick[]>([]);
  const [gameById, setGameById] = useState<Record<string, PickDetailGameInfo>>({});
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<{ name: string; hit: number; miss: number }[]>([]);
  const [useLocalPicks, setUseLocalPicks] = useState(false);

  // Refresh current filters
  const refresh = () => {
    setPageNum(1);
    setTotal(0);
    setError(null);
    setFilters((f) => ({ ...f }));
  };

  // Reset accumulated picks whenever filters change. URL changes trigger
  // filter state via the useEffect below.
  const resetAndLoad = useCallback(
    (nextFilters: PicksFilterValues) => {
      if (nextFilters.sport !== filters.sport) {
        setPicks([]);
        setPageNum(1);
        setTotal(0);
        setError(null);
        setFilters(nextFilters);
        setUseLocalPicks(false);
      } else {
        setFilters(nextFilters);
        setUseLocalPicks(true);
      }
    },
    [filters.sport],
  );

  useEffect(() => {
    if (!userGroups.includes(GROUP_NAMES.elite)) {
      navigate('/picks');
    }
  }, [userGroups, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listGames({ sport: filters.sport, batchSize: 200, pageNum: 0 })
      .then((res: ApiGameListResponse) => {
        if (cancelled) return;
        const gameById: Record<string, PickDetailGameInfo> = {};
        res.results.forEach((game: ApiGame) => {
          gameById[game.eventId] = {
            eventId: game.eventId,
            eventName: game.eventName.replace('Athletics Athletics', 'Athletics'),
            eventStart: game.scheduledAt,
          };
        });
        setGameById({ ...gameById });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load games';
        setError(String(message));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.sport]);

  // Fetch on (filters | pageNum) change. Append for page > 1.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    if (useLocalPicks) {
      setUseLocalPicks(false);
      const updatedAllPicks = (results: ApiPick[]) => {
        if (filters.parlayLegs) {
          results = results.filter((pick) => pick.parlayLegs === filters.parlayLegs);
        }
        return results.sort((a, b) => (a.computedDate >= b.computedDate ? -1 : 1));
      };
      setAllPicks(updatedAllPicks);
      const recentLockPicks = updatedAllPicks(allPicks)
        .filter((pick) => pick.riskLevel === 'lock' && pick.outcome === 'hit')
        .sort((a, b) => (a.computedDate >= b.computedDate ? -1 : 1))
        .slice(0, 50); // most recent 50 locks displayed
      setTotal(recentLockPicks.length);
      setPicks(recentLockPicks);
      setError(null);
      setLoading(false);
      return;
    }
    listPicks({ ...filters, batchSize: 350, pageNum, outcome: 'hit' })
      .then((res) => {
        if (cancelled) return;
        listPicks({ ...filters, batchSize: 350, pageNum, outcome: 'miss' }).then((missRes) => {
          if (cancelled) return;
          const combinedResults = [...res.results, ...missRes.results];
          const gameIds = combinedResults.reduce((acc, pick) => {
            acc.add(pick.eventId);
            return acc;
          }, new Set<string>());
          // Mutate the picks in-place to add the game name for display in the UI.
          combinedResults.forEach((pick: ApiPick) => {
            if (gameIds.has(pick.eventId)) {
              const gameName = gameById[pick.eventId]?.eventName;
              pick.eventName = gameName ? gameName : pick.eventId; // fallback to ID if name is missing
            }
          });
          const updatedAllPicks = (prev: ApiPick[]) => {
            let results = pageNum === 1 ? combinedResults : [...prev, ...combinedResults];
            if (filters.parlayLegs) {
              results = results.filter((pick) => pick.parlayLegs === filters.parlayLegs);
            }
            return results.sort((a, b) => (a.computedDate >= b.computedDate ? -1 : 1));
          };
          setAllPicks(updatedAllPicks);
          const recentLockPicks = updatedAllPicks(allPicks)
            .filter((pick) => pick.riskLevel === 'lock' && pick.outcome === 'hit')
            .sort((a, b) => (a.computedDate >= b.computedDate ? -1 : 1))
            .slice(0, 50); // most recent 50 locks displayed
          setTotal(recentLockPicks.length);
          setPicks(recentLockPicks);
          setError(null);
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load picks';
        setError(String(message));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gameById, filters, pageNum, useLocalPicks]);

  useEffect(() => {
    const hitCountByDay = allPicks
      .sort((a, b) => new Date(a.computedDate).getTime() - new Date(b.computedDate).getTime())
      .reduce((acc, pick) => {
        const date = new Date(pick.computedDate).toLocaleDateString('UTC', {
          day: 'numeric',
          month: 'numeric',
          timeZone: 'UTC',
        });
        if (!acc[date]) acc[date] = 0;
        if (pick.outcome === 'hit') acc[date] += 1;
        return acc;
      }, {} as Record<string, number>);
    const missCountByDay = allPicks
      .sort((a, b) => new Date(a.computedDate).getTime() - new Date(b.computedDate).getTime())
      .reduce((acc, pick) => {
        const date = new Date(pick.computedDate).toLocaleDateString('UTC', {
          day: 'numeric',
          month: 'numeric',
          timeZone: 'UTC',
        });
        if (!acc[date]) acc[date] = 0;
        if (pick.outcome === 'miss') acc[date] += 1;
        return acc;
      }, {} as Record<string, number>);
    const chartDataByDate = Object.keys(hitCountByDay).map((date) => ({
      name: date,
      hit: hitCountByDay[date] || 0,
      miss: missCountByDay[date] || 0,
    }));
    setChartData(chartDataByDate);
  }, [filters, allPicks]);

  const handleRefresh = () => {
    refresh();
  };

  const handleFilterChange = (next: PicksFilterValues) => {
    if (next.parlayLegs !== filters.parlayLegs || next.sport !== filters.sport) {
      resetAndLoad(next);
    }
  };

  const Chart = () => (
    <LineChart
      style={{ width: '100%', maxWidth: '100vw', maxHeight: '50vh', aspectRatio: 1.718 }}
      responsive
      data={chartData}
      margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0,
      }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis stroke={'#fffbfb'} fontSize={14} dataKey="name" />
      <YAxis stroke={'#fffbfb'} fontSize={14} />
      <Tooltip
        cursor={{
          stroke: 'var(--color-border-2)',
        }}
        contentStyle={{
          backgroundColor: 'rgb(61 25 0 / 75%)',
          borderColor: 'var(--color-border-2)',
        }}
      />
      <Line
        type="monotone"
        dataKey="hit"
        stroke="#82ca9d"
        dot={{ fill: '#82ca9d' }}
        activeDot={{ stroke: '#82ca9d' }}
      />
      <Line
        type="monotone"
        dataKey="miss"
        stroke="#ca8282"
        dot={{ fill: '#ca8282' }}
        activeDot={{ stroke: '#ca8282' }}
      />
    </LineChart>
  );

  return (
    <div className={'pickInsights'}>
      <div className={'pickInsights__header'}>
        <div>{`${t('pages.pickInsights.headerText')}`}</div>
        <div className={'pickInsights__header--subtext'}>{`${t(
          'pages.pickInsights.headerSubtext',
        )}`}</div>
      </div>
      {loading && chartData.length === 0 ? (
        <div className={'pick-detail__loading'}>
          <Spinner />
        </div>
      ) : (
        <div className={'pickInsights__chart'}>
          <Chart />
        </div>
      )}
      <PicksFilters
        value={filters}
        userGroups={userGroups}
        onChange={handleFilterChange}
        onRefresh={handleRefresh}
        disabled={loading}
        isInsightsPage={true}
      />
      <div className={'pickInsights__meta'}>
        {loading && picks.length === 0 ? (
          <div className={'pick-detail__loading'}>
            <Spinner />
          </div>
        ) : (
          <span>{`recently hit picks (${total})`}</span>
        )}
      </div>

      {error && (
        <div className={'pickInsights__error'} role={'alert'}>
          {error}
        </div>
      )}

      <div className={'pickInsights__list'}>
        {picks.map((p) => (
          <PickCard key={p.pickId} pick={p} currentFilters={filters} isInsightsPage={true} />
        ))}

        {!loading && !error && picks.length === 0 && (
          <div className={'pickInsights__empty'}>{`${t(`pages.picks.noResults`)}`}</div>
        )}
      </div>
    </div>
  );
}

export default PickInsights;
